import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';
import { Order, UserAddress, OrderItem } from '../types';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  Divider,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  ArrowBack, 
  LocalShipping, 
  LocationOn, 
  Schedule,
  Receipt,
  Person,
  Launch,
  AssignmentReturn,
  Info,
  Email,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { colors } from '../utils/constants';

// Approval Section Component
interface ApprovalSectionProps {
  orderId: number;
  orderNumber: string;
  order: Order;
}

const ApprovalSection: React.FC<ApprovalSectionProps> = ({ orderId, orderNumber, order: orderData }) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleApproval = async (approved: boolean) => {
    setIsLoading(true);
    try {
      const newStatus = approved ? 'approved' : 'cancelled';

      // Prepare update payload
      const updatePayload: any = { status: newStatus };

      // If the order has a return_method that requires return_pickup_datetime,
      // include the existing return_pickup_datetime to avoid validation errors
      if (orderData.return_method === 'pickup' || orderData.return_method === 'self_drop_off') {
        if (orderData.return_pickup_datetime) {
          updatePayload.return_pickup_datetime = orderData.return_pickup_datetime;
        }
      }

      await api.patch(`/orders/${orderId}`, updatePayload);

      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'warning.dark' }}>
        ⚠️ Order Changes Require Approval
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'warning.dark' }}>
        The administrator has made changes to your order #{orderNumber}. Please review and approve or reject these changes.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={() => handleApproval(true)}
          disabled={isLoading}
          startIcon={<Info />}
          sx={{
            bgcolor: 'success.main',
            '&:hover': { bgcolor: 'success.dark' }
          }}
        >
          {isLoading ? 'Processing...' : 'Approve Changes'}
        </Button>

        <Button
          variant="outlined"
          color="error"
          size="large"
          onClick={() => handleApproval(false)}
          disabled={isLoading}
          startIcon={<AssignmentReturn />}
          sx={{
            borderColor: 'error.main',
            color: 'error.main',
            '&:hover': {
              borderColor: 'error.dark',
              bgcolor: 'error.50'
            }
          }}
        >
          {isLoading ? 'Processing...' : 'Reject & Cancel Order'}
        </Button>
      </Box>

      <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
        Note: Approving changes will proceed with the order as modified. Rejecting will cancel the entire order.
      </Typography>
    </Box>
  );
};

const fetchOrderDetails = async (orderId: string): Promise<Order> => {
  const { data } = await api.get(`/orders/${orderId}`);
  return data;
};


const AddressCard: React.FC<{ address: UserAddress | null, title: string, icon: React.ReactNode }> = ({ 
  address, 
  title, 
  icon 
}) => (
  <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'grey.300' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon}
        <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {address ? (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {address.address_line_1}
          </Typography>
          {address.address_line_2 && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {address.address_line_2}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {`${address.city}, ${address.state} ${address.postal_code}`}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {address.country}
          </Typography>
          {address.phone_number && (
            <Typography variant="body2" color="text.secondary">
              {address.phone_number}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Not available
        </Typography>
      )}
    </CardContent>
  </Card>
);

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { data: order, isLoading, isError, error } = useQuery<Order, Error>({
    queryKey: ['order', id],
    queryFn: () => fetchOrderDetails(id!),
    enabled: !!id,
  });


  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} sx={{ color: colors.primary }} />
        </Box>
      </Container>
    );
  }

  const getTimeWindowLabel = (isoString: string): string => {
    // Convert to Pacific time first — datetimes are stored as UTC in the DB and the
    // hour value in the raw ISO string won't match the Pacific hour the user selected
    if (!isoString) return '';
    const pacificHour = utcToZonedTime(new Date(isoString), 'America/Vancouver').getHours();
    switch (pacificHour) {
      case 8:  return 'Before 9am';
      case 9:  return '9am-1pm';
      case 13: return '1pm-5pm';
      case 17: return 'After 5pm';
      default: return '';
    }
  };

  if (isError) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
          Error fetching order details: {error.message}
        </Alert>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" gutterBottom>
            Order not found
          </Typography>
          <Button onClick={() => navigate('/orders')} variant="outlined">
            Back to Orders
          </Button>
        </Box>
      </Container>
    );
  }

  const subtotal = order.subtotal_cents / 100;
  const shipping = order.shipping_cents / 100;
  const tax = order.tax_cents / 100;
  const serviceFee = order.service_fee_cents / 100;
  const total = order.total_cents / 100;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      case 'shipped': return 'info';
      case 'approved': return 'primary';
      case 'request_changes': return 'warning';
      default: return 'default';
    }
  };

  console.log('Order object:', order);
  console.log('Order keys:', Object.keys(order));
  console.log('return_method:', order.return_method);
  console.log('return_pickup_datetime:', order.return_pickup_datetime);

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header with Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/orders')}
          sx={{ 
            mb: 4,
            color: colors.primary,
            '&:hover': { backgroundColor: `${colors.primary}10` }
          }}
        >
          Back to Orders
        </Button>

        {/* Page Title */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 700,
              color: 'primary.main',
              mb: 1
            }}
          >
            Order #{order.order_number}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Placed on {format(new Date(order.created_at), 'PPP')}
          </Typography>
        </Box>

        {/* Approval Section - Moved Here */}
        {order.status === 'request_changes' && (
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: 'warning.main',
              mb: 4,
              bgcolor: 'warning.50',
              borderWidth: 2
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <ApprovalSection orderId={order.id} orderNumber={order.order_number} order={order} />
            </CardContent>
          </Card>
        )}

        {/* Order Status and Details */}
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderColor: 'grey.300',
            mb: 4
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Order Status & Details
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label={order.status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(order.status)}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                icon={order.delivery_option === 'pickup' ? <LocationOn /> : <LocalShipping />}
                label={order.delivery_option === 'pickup' ? 'Pickup' : 'Delivery'}
                variant="outlined"
              />
            </Stack>
            
            {order.delivery_option === 'pickup' && (order.pickup_datetime || order.delivery_date) && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  <strong>Pickup Date:</strong> {format(new Date(order.pickup_datetime || order.delivery_date!), 'PPP')}
                  {order.pickup_datetime && getTimeWindowLabel(order.pickup_datetime) && ` ${getTimeWindowLabel(order.pickup_datetime)}`}
                  {!order.pickup_datetime && order.delivery_time && ` at ${order.delivery_time}`}
                </Typography>
              </Box>
            )}
            
            {order.delivery_option === 'delivery' && (order.delivery_datetime || order.delivery_date) && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalShipping sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  <strong>Delivery Date:</strong> {format(new Date(order.delivery_datetime || order.delivery_date!), 'PPP')}
                  {order.delivery_datetime && getTimeWindowLabel(order.delivery_datetime) && ` ${getTimeWindowLabel(order.delivery_datetime)}`}
                  {!order.delivery_datetime && order.delivery_time && ` at ${order.delivery_time}`}
                </Typography>
              </Box>
            )}

            
            {/* Payment Method */}
            {order.payment_method && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Receipt sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  <strong>Payment Method:</strong> {order.payment_method}
                </Typography>
              </Box>
            )}

            {/* Trusted Contact */}
            {order.trusted_contact && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  <strong>Customer Contact:</strong> {order.trusted_contact.name}
                  {order.trusted_contact.title && ` (${order.trusted_contact.title})`}
                  <br />
                  <Typography variant="body2" component="span" color="text.secondary">
                    {order.trusted_contact.email}
                    {order.trusted_contact.phone_number && ` • ${order.trusted_contact.phone_number}`}
                  </Typography>
                </Typography>
              </Box>
            )}

            {order.notes && order.notes.trim() && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                  Order Notes:
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  "{order.notes}"
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Customer Information */}
        {order.user && (
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: 'grey.300',
              mb: 4
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: colors.primary }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Customer Information
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {order.user.first_name} {order.user.last_name}
                </Typography>
              </Box>

              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <Email sx={{ mr: 1, fontSize: 16, verticalAlign: 'middle' }} />
                  {order.user.email}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Shipping & Returns Details */}
        <Card 
          variant="outlined" 
          sx={{ 
            borderRadius: 3, 
            borderColor: 'grey.300',
            mb: 4 
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Shipping & Returns
            </Typography>
            
            {/* Shipping Method */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {order.delivery_option === 'pickup' ? <LocationOn sx={{ mr: 1, color: 'text.secondary' }} /> : <LocalShipping sx={{ mr: 1, color: 'text.secondary' }} />}
              <Typography variant="body1">
                <strong>Shipping Method:</strong> {order.delivery_option === 'pickup' ? 'Pickup' : 'Delivery'}
              </Typography>
            </Box>

            {/* Shipping Cost */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Receipt sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                <strong>Shipping Cost:</strong> ${(order.shipping_cents / 100).toFixed(2)}
              </Typography>
            </Box>

            {/* Pickup Location for Store Pickup */}
            {order.delivery_option === 'pickup' && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <LocationOn sx={{ mr: 1, color: 'text.secondary', mt: 0.5 }} />
                <Typography variant="body1">
                  <strong>Pickup Location:</strong><br />
                  69 West 3rd Ave, Vancouver BC, V5Y3T8
                </Typography>
              </Box>
            )}

            {/* Return Information */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
              Return Information
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AssignmentReturn sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                <strong>Return Method:</strong> {
                  order.return_method === 'pickup' ? 'Pickup Service' :
                  order.return_method === 'self_drop_off' ? 'Self Return' :
                  order.return_method === 'with_next_delivery' ? 'With Next Delivery' :
                  'Not Specified'
                }
              </Typography>
            </Box>

            {order.return_pickup_datetime && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  <strong>{order.return_method === 'pickup' ? 'Return Pickup Date:' : 'Return Drop-off Date:'}</strong> {format(new Date(order.return_pickup_datetime), 'PPP')}{getTimeWindowLabel(order.return_pickup_datetime) && ` ${getTimeWindowLabel(order.return_pickup_datetime)}`}
                </Typography>
              </Box>
            )}

            {order.return_method === 'with_next_delivery' && (
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'success.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.200',
                display: 'flex', 
                alignItems: 'center' 
              }}>
                <Info sx={{ mr: 1, color: 'success.600' }} />
                <Typography variant="body2" color="success.800">
                  <strong>Returns will be collected with your next delivery.</strong>
                </Typography>
              </Box>
            )}

            {!order.return_method && (
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'info.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.200'
              }}>
                <Typography variant="body2" color="info.dark" sx={{ fontWeight: 500 }}>
                  ℹ️ Return method will be coordinated separately or customer will self-return
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
        
        {/* Order Items */}
        <Card 
          variant="outlined" 
          sx={{ 
            borderRadius: 3, 
            borderColor: 'grey.300',
            mb: 4 
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Items ({order.order_items.length})
            </Typography>
            <List disablePadding>
              {order.order_items.map((item: OrderItem, index: number) => (
                <React.Fragment key={item.id}>
                  <ListItem 
                    sx={{ 
                      px: 0,
                      py: 2,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                      {/* Product Image */}
                      <Box 
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: 'grey.50',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          flexShrink: 0,
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={item.product_snapshot?.image_url || '/placeholder-product.png'}
                          alt={item.product_name}
                          sx={{
                            objectFit: 'contain',
                            height: '100%',
                            width: 'auto',
                            maxWidth: '100%',
                          }}
                          onError={(e) => {
                            e.currentTarget.src = '/placeholder-product.png';
                          }}
                        />
                      </Box>

                      {/* Product Details */}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600,
                              cursor: 'pointer',
                              color: colors.primary,
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => navigate(`/products/${item.product_snapshot?.id || ''}`)}
                          >
                            {item.product_name}
                            <Tooltip title="View Product Details">
                              <IconButton size="small" sx={{ ml: 1 }}>
                                <Launch fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.primary }}>
                            ${(item.total_price_cents / 100).toFixed(2)}
                          </Typography>
                        </Box>
                        
                        {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {item.product_snapshot?.description || 'No description available'}
                        </Typography> */}
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2">
                            <strong>Quantity:</strong> {item.quantity}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ${(item.unit_price_cents / 100).toFixed(2)} each
                          </Typography>
                        </Box>

                        {item.product_sku && (
                          <Typography variant="caption" color="text.secondary">
                            SKU: {item.product_sku}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                  {index < order.order_items.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Addresses */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
          <AddressCard 
            address={order.shipping_address as UserAddress | null} 
            title="Shipping Address"
            icon={<LocalShipping sx={{ color: colors.primary }} />}
          />
          <AddressCard 
            address={order.billing_address as UserAddress | null} 
            title="Billing Address"
            icon={<Person sx={{ color: colors.primary }} />}
          />
        </Box>

        {/* Trusted Contact Information */}
        {order.trusted_contact_id && order.trusted_contact && (
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: 'grey.300',
              mb: 4
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: colors.primary }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Customer Contact
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {order.trusted_contact.name}
                </Typography>
                {order.trusted_contact.title && (
                  <Typography variant="body2" color="text.secondary">
                    {order.trusted_contact.title}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Email: {order.trusted_contact.email}
                </Typography>
              </Box>

              {order.trusted_contact.phone_number && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Phone: {order.trusted_contact.phone_number}
                  </Typography>
                </Box>
              )}

              {order.contact_preference && (
                <Box sx={{
                  p: 2,
                  backgroundColor: 'success.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'success.200'
                }}>
                  <Typography variant="body2" color="success.dark" sx={{ fontWeight: 500 }}>
                    ✓ Customer requested to be contacted regarding this order
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order Summary */}
        <Card 
          variant="outlined" 
          sx={{ 
            borderRadius: 3, 
            borderColor: 'grey.300',
            maxWidth: { xs: '100%', md: 400 },
            ml: { xs: 0, md: 'auto' }
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Receipt sx={{ mr: 1, color: colors.primary }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Order Summary
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1">Subtotal</Typography>
              <Typography variant="body1">${subtotal.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1">Shipping</Typography>
              <Typography variant="body1">${shipping.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1">Tax</Typography>
              <Typography variant="body1">${tax.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1">Service Fee</Typography>
              <Typography variant="body1">${serviceFee.toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 2 }}/>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary }}>
                ${total.toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default OrderDetailPage; 